/**
 * AssessmentGenerator.ts
 *
 * Shared utility for generating assessments from trajectories.
 * This ensures both ReportProcessor and ReportAssessmentValidator use the exact same logic.
 */

import * as M from "reports/measures/Measure";
import * as MD from "reports/measures/MeasureDef";
import * as T from "reports/measures/Trajectory";
import * as SR from "reports/measures/SeverityResolver";

export type AssessmentCategory = "physical" | "behavioral" | "social" | "continuedNeeds" | "excluded";

export interface AssessmentDecision {
    trajectory: T.Trajectory;
    category: AssessmentCategory;
    reason: string;
    details: {
        isIncluded: boolean;
        bandSeverityTrajectory: T.TrajectoryType;
        isFunctional: boolean;
        isPhysical: boolean;
        isBehavioral: boolean;
        isSocial: boolean;
        improvedScoreBand: boolean;
    };
}

export interface AssessmentResults {
    physical: string;
    behavioral: string;
    social: string;
    continuedNeeds: string;
    counts: {
        physical: number;
        behavioral: number;
        social: number;
        continuedNeeds: number;
    };
    decisions: AssessmentDecision[];
}

/**
 * Aggregate ROM assessments by grouping them by severity level
 * Handles Upper, Lower, and Neck ROM types
 */
export function aggregateUpperRomAssessments(trajectories: T.Trajectories): string[] {
    const romTrajectories: T.Trajectories = [];
    const otherTrajectories: T.Trajectories = [];

    // Separate all ROM types from other assessments
    trajectories.forEach(
        trajectory => {
            if (M.isUpperRomType(trajectory.measureType) || M.isLowerRomType(trajectory.measureType) || M.isNeckRomType(trajectory.measureType)) {
                romTrajectories.push(trajectory);
            }
            else {
                otherTrajectories.push(trajectory);
            }
        }
    );

    if (romTrajectories.length === 0) {
        return trajectories.map(trajectory => T.displayBandTrajectoryTerseAssessment(trajectory));
    }

    // Group ROM assessments by severity level
    const severityGroups = new Map<string, T.Trajectories>();
    romTrajectories.forEach(
        (trajectory: T.Trajectory) => {
            const resolver: SR.SeverityDetails = M.severityResolver(trajectory.comparedMeasure);
            const severityKey = `${resolver.severity}`;
            const group = severityGroups.get(severityKey);
            if (group) {
                group.push(trajectory);
            }
            else {
                severityGroups.set(severityKey, [trajectory]);
            }
        }
    );

    // Create aggregated assessments
    const aggregatedAssessments: string[] = [];

    severityGroups.forEach((group: T.Trajectories) => {
        if (group.length === 1) {
            // Single assessment, no aggregation needed
            aggregatedAssessments.push(T.displayBandTrajectoryTerseAssessment(group[0]));
        }
        else {
            // Aggregate assessments with the same severity
            const firstTrajectory = group[0];
            const resolver: SR.SeverityDetails = M.severityResolver(firstTrajectory.comparedMeasure);

            // Extract individual measure descriptions (before the severity info)
            const measureDescriptions = group.map(trajectory => {
                const scoreDescription = M.scoreDescription(trajectory.comparedMeasure);
                const scoreRangeDisplay = M.scoreRangeDisplay(trajectory.comparedMeasure);
                const measureName = scoreDescription ? [scoreDescription, scoreRangeDisplay].join(" ") : trajectory.measureName;
                return `${measureName} ${T.displayBandTrajectorySlope(trajectory)} ${M.displayAsUnits(trajectory.comparedMeasure)} (${resolver.label})`;
            });

            // Combine all measure descriptions with shared severity description (no line breaks between measures)
            const combinedAssessment = `${measureDescriptions.join(" ")}.\n${resolver.description}`;
            aggregatedAssessments.push(combinedAssessment);
        }
    });

    // Return aggregated ROM assessments plus other assessments (both already in sorted order)
    return [...aggregatedAssessments, ...otherTrajectories.map(trajectory => T.displayBandTrajectoryTerseAssessment(trajectory))];
}

/**
 * Generate assessments from trajectories and inclusion settings
 * This is the core logic extracted from ReportProcessor.generateAssessments
 */
export function generateAssessments(
    allTrajectories: T.Trajectories,
    includedTrajectories: Record<MD.MeasureType, boolean>
): AssessmentResults {
    const physicalTrajectories: T.Trajectories = [];
    const behavioralTrajectories: T.Trajectories = [];
    const socialTrajectories: T.Trajectories = [];
    const continuedNeedsTrajectories: T.Trajectories = [];
    const decisions: AssessmentDecision[] = [];

    allTrajectories.forEach((trajectory: T.Trajectory) => {
        const isIncluded = includedTrajectories[trajectory.measureType];

        // Skip trajectories with null comparedMeasure
        if (!trajectory.comparedMeasure) {
            decisions.push({
                trajectory,
                category: "excluded",
                reason: "No compared measure available",
                details: {
                    isIncluded,
                    bandSeverityTrajectory: trajectory.bandSeverityTrajectory,
                    isFunctional: false,
                    isPhysical: M.isPhysicalType(trajectory.measureType),
                    isBehavioral: M.isBehavioralType(trajectory.measureType),
                    isSocial: M.isSocialType(trajectory.measureType),
                    improvedScoreBand: false,
                },
            });
            return;
        }

        const isFunctional = T.isFunctional(trajectory);
        const isPhysical = M.isPhysicalType(trajectory.measureType);
        const isBehavioral = M.isBehavioralType(trajectory.measureType);
        const isSocial = M.isSocialType(trajectory.measureType);
        const improvedScoreBand = trajectory.bandSeverityTrajectory === T.TrajectoryType.IMPROVED;

        const details = {
            isIncluded,
            bandSeverityTrajectory: trajectory.bandSeverityTrajectory,
            isFunctional,
            isPhysical,
            isBehavioral,
            isSocial,
            improvedScoreBand,
        };

        // Not included in the report
        if (!isIncluded) {
            decisions.push({
                trajectory,
                category: "excluded",
                reason: "Not included in report (unchecked in inclusions)",
                details,
            });
            return;
        }

        // Rule: Physical Assessments include physical measures that improved by at least one score band
        // Rule: Behavioral Assessments include behavioral measures that improved by at least one score band
        // Rule: Social Assessments include social measures that improved by at least one score band
        if (improvedScoreBand) {
            // Physical: improved AND physical type
            if (isPhysical) {
                physicalTrajectories.push(trajectory);
                decisions.push({
                    trajectory,
                    category: "physical",
                    reason: "Physical + Improved into higher score band",
                    details,
                });
            }
            // Behavioral: improved AND behavioral type (regardless of functional status)
            else if (isBehavioral) {
                behavioralTrajectories.push(trajectory);
                decisions.push({
                    trajectory,
                    category: "behavioral",
                    reason: "Behavioral + Improved into higher score band",
                    details,
                });
            }
            // Social: improved AND social type (regardless of functional status)
            else if (isSocial) {
                socialTrajectories.push(trajectory);
                decisions.push({
                    trajectory,
                    category: "social",
                    reason: "Social + Improved into higher score band",
                    details,
                });
            }
            // Improved measures that don't fit Physical/Behavioral/Social categories are excluded
            else {
                decisions.push({
                    trajectory,
                    category: "excluded",
                    reason: "Improved into higher score band (no appropriate category)",
                    details,
                });
            }
        }
        // Rule: The Continued Needs section includes all non-functional measures that declined or remained in the same score band
        else {
            const declined = trajectory.bandSeverityTrajectory === T.TrajectoryType.WORSENED;
            const unchanged = trajectory.bandSeverityTrajectory === T.TrajectoryType.UNCHANGED;

            // Only non-functional measures that declined or stayed the same go to Continued Needs
            if (!isFunctional && (declined || unchanged)) {
                continuedNeedsTrajectories.push(trajectory);
                decisions.push({
                    trajectory,
                    category: "continuedNeeds",
                    reason: declined
                        ? "Declined by at least one score band + Non-functional"
                        : "Remained in same score band + Non-functional",
                    details,
                });
            } else {
                decisions.push({
                    trajectory,
                    category: "excluded",
                    reason: isFunctional
                        ? "Did not improve into higher score band + Functional (no continued need)"
                        : "Did not improve into higher score band + Non-functional (but functional measures excluded from continued needs)",
                    details,
                });
            }
        }
    });

    // Helper to get the display name used in the assessment text
    const getDisplayName = (trajectory: T.Trajectory): string => {
        const scoreDescription = M.scoreDescription(trajectory.comparedMeasure);
        const scoreRangeDisplay = M.scoreRangeDisplay(trajectory.comparedMeasure);
        return scoreDescription ? [scoreDescription, scoreRangeDisplay].join(" ") : trajectory.measureName;
    };

    // Sort function: ROM measures first (alphabetically by display name), then others (alphabetically by display name)
    const sortTrajectories = (trajectories: T.Trajectories): T.Trajectories => {
        const romTrajectories = trajectories.filter(t => M.isUpperRomType(t.measureType) || M.isLowerRomType(t.measureType) || M.isNeckRomType(t.measureType));
        const otherTrajectories = trajectories.filter(t => !M.isUpperRomType(t.measureType) && !M.isLowerRomType(t.measureType) && !M.isNeckRomType(t.measureType));

        romTrajectories.sort((a, b) => getDisplayName(a).localeCompare(getDisplayName(b)));
        otherTrajectories.sort((a, b) => getDisplayName(a).localeCompare(getDisplayName(b)));

        return [...romTrajectories, ...otherTrajectories];
    };

    physicalTrajectories.splice(0, physicalTrajectories.length, ...sortTrajectories(physicalTrajectories));
    behavioralTrajectories.splice(0, behavioralTrajectories.length, ...sortTrajectories(behavioralTrajectories));
    socialTrajectories.splice(0, socialTrajectories.length, ...sortTrajectories(socialTrajectories));
    continuedNeedsTrajectories.splice(0, continuedNeedsTrajectories.length, ...sortTrajectories(continuedNeedsTrajectories));

    const aggregatedPhysicalAssessments: string[] = aggregateUpperRomAssessments(physicalTrajectories);
    const physicalAssessmentsText = aggregatedPhysicalAssessments.join("\n\n");

    const behavioralAssessmentsText = behavioralTrajectories
        .map(trajectory => T.displayBandTrajectoryTerseAssessment(trajectory))
        .join("\n\n");

    const socialAssessmentsText = socialTrajectories
        .map(trajectory => T.displayBandTrajectoryTerseAssessment(trajectory))
        .join("\n\n");

    const continuedNeedsAssessmentsText = continuedNeedsTrajectories
        .map(trajectory => T.displayBandTrajectoryTerseAssessment(trajectory))
        .join("\n\n");

    return {
        physical: physicalAssessmentsText.trim(),
        behavioral: behavioralAssessmentsText.trim(),
        social: socialAssessmentsText.trim(),
        continuedNeeds: continuedNeedsAssessmentsText.trim(),
        counts: {
            physical: physicalTrajectories.length,
            behavioral: behavioralTrajectories.length,
            social: socialTrajectories.length,
            continuedNeeds: continuedNeedsTrajectories.length,
        },
        decisions,
    };
}
